import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { ApiResponse } from "@entrilia/shared/api/types";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DocumentDialogIcon from "@entrilia/shared/icons/DocumentDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";

interface Props {
  getFileDownloadInfoCallback: () => Promise<ApiResponse<FileDownloadInfo>>;
  fileIdentifier?: string;
}

const NotPossibleDocumentPreview = ({ getFileDownloadInfoCallback, fileIdentifier }: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const handleDownloadClick = async () => {
    const resp = await getFileDownloadInfoCallback();
    if (resp.data !== undefined) {
      await downloadFileFromUrl(resp.data.downloadUrl);
    } else {
      sendNotificationError(resp.error?.message ?? "Failed to download document");
      logError(
        `Could not receive download URL for document: ${fileIdentifier}. Error: ${resp.error?.message}`,
        "NotPossibleDocumentPreview"
      );
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 1,
        width: "100%",
        height: "100%",
      }}
    >
      <Stack spacing={2} sx={{ alignItems: "center" }}>
        <DocumentDialogIcon sx={{ width: 80, height: 80 }} />
        <Stack sx={{ alignItems: "center" }}>
          <Typography color="textSecondary" sx={{ textAlign: "center" }}>
            This file cannot be previewed. Please download it.
          </Typography>
        </Stack>
        <Button color="primary" variant="outlined" onClick={handleDownloadClick} startIcon={<SaveIcon />}>
          Download file
        </Button>
      </Stack>
    </Box>
  );
};

export default NotPossibleDocumentPreview;
